import { BusinessRegistrationService } from '../../../../_services/BusinessRegistration.Service';
import {
  BR_STATUS,
  getItemStatusCode,
  isBrPendingCode,
  isBrApprovedCode,
  sortBrListItemsByLastUpdated,
  normalizeBrApiItem,
} from '../../../businessDocuments/utils/brDocument';
import {
  composeBrListWithNameChanges,
  getApprovedBrForNameChangeOptions,
} from '../../../businessDocuments/utils/brNameChange';

export function normalizeAdminItem(row, listKind, requestKindFallback) {
  const requestKind = row.requestKind || row.RequestKind || requestKindFallback || 'BR_DOCUMENT';
  const base = normalizeBrApiItem(row, { listKind, requestKind });
  return {
    ...base,
    listKind,
    requestKind,
    sourceApprovalId: row.sourceApprovalId ?? row.SourceApprovalId ?? null,
    sourceBusinessRegistrationId:
      row.sourceBusinessRegistrationId ?? row.SourceBusinessRegistrationId ?? null,
    resultApprovalId: row.resultApprovalId ?? row.ResultApprovalId ?? null,
    resultBusinessRegistrationId:
      row.resultBusinessRegistrationId ?? row.ResultBusinessRegistrationId ?? null,
    previousDisplayNameEn: row.previousDisplayNameEn || row.PreviousDisplayNameEn || '',
    previousDisplayNameZh: row.previousDisplayNameZh || row.PreviousDisplayNameZh || '',
    isNameChangePending: !!(row.isNameChangePending || row.IsNameChangePending),
    isNameChangeRequestCard: !!(row.isNameChangeRequestCard || row.IsNameChangeRequestCard),
    isNameChangeResultCard: !!(row.isNameChangeResultCard || row.IsNameChangeResultCard),
    isSupersededByNameChange: !!(row.isSupersededByNameChange || row.IsSupersededByNameChange),
  };
}

export const BR_ADMIN_REJECT_REASON_CODES = [
  { code: 'DOC_UNCLEAR', key: 'lbl_EMPBE_BR_Admin_Reject_DocUnclear' },
  { code: 'NAME_MISMATCH', key: 'lbl_EMPBE_BR_Admin_Reject_NameMismatch' },
  { code: 'REG_MISMATCH', key: 'lbl_EMPBE_BR_Admin_Reject_RegMismatch' },
  { code: 'EXPIRED', key: 'lbl_EMPBE_BR_Admin_Reject_Expired' },
  // OTHER 放最后，避免回填失败时视觉上像「总是勾选第一项」
  { code: 'OTHER', key: 'lbl_EMPBE_BR_Admin_Reject_Other' },
];

/**
 * 把已落库的驳回原因文案还原成管理端草稿勾选。
 * 存库格式为模板文案（或手输）以 "; " 拼接；能匹配当前语言模板的勾对应项，否则进 OTHER。
 */
export function parseAdminRejectDraftFromSavedText(reasonText, options = []) {
  const text = String(reasonText || '').trim();
  if (!text) {
    return { reasons: [], otherReasonText: '' };
  }
  const labelToCode = new Map();
  (options || []).forEach((opt) => {
    if (!opt?.code || opt.code === 'OTHER') {
      return;
    }
    const label = String(opt.label || '').trim();
    if (label) {
      labelToCode.set(label.toLowerCase(), opt.code);
    }
  });

  const reasons = [];
  const otherParts = [];
  text.split(/\s*;\s*/).forEach((part) => {
    const chunk = String(part || '').trim();
    if (!chunk) {
      return;
    }
    const code = labelToCode.get(chunk.toLowerCase());
    if (code) {
      if (!reasons.includes(code)) {
        reasons.push(code);
      }
      return;
    }
    otherParts.push(chunk);
  });

  if (otherParts.length && !reasons.includes('OTHER')) {
    reasons.push('OTHER');
  }
  return {
    reasons,
    otherReasonText: otherParts.join('; '),
  };
}

export function toAdminDisplayItem(item, listKind) {
  let requestKind = item.requestKind || 'BR_DOCUMENT';
  if (
    item.isNameChangeRequestCard
    || item.isNameChangeResultCard
    || item.isNameChangePending
    || item.requestKind === 'NAME_CHANGE'
    || item.requestKind === 'NAME_CHANGE_RESULT'
    || item.RequestKind === 'NAME_CHANGE'
    || item.RequestKind === 'NAME_CHANGE_RESULT'
  ) {
    requestKind = item.isNameChangeResultCard || item.requestKind === 'NAME_CHANGE_RESULT'
      ? 'NAME_CHANGE_RESULT'
      : 'NAME_CHANGE';
  }
  return normalizeAdminItem({ ...item, requestKind }, listKind, requestKind);
}

function emptyAdminLoadResult(extra = {}) {
  return {
    pendingItems: [],
    processedItems: [],
    documentList: [],
    nameChangeSourceList: [],
    currentNameEn: '',
    currentNameZh: '',
    error: null,
    listLoadWarning: null,
    ...extra,
  };
}

function findNameChangeSourceBr(item, brItems) {
  return (brItems || []).find(
    (br) => String(br.approvalId) === String(item.sourceApprovalId)
      || String(br.businessRegistrationId) === String(
        item.sourceBusinessRegistrationId || item.businessRegistrationId,
      ),
  );
}

function resolveNameChangeDisplayStatus(item, listKind) {
  const raw = getItemStatusCode(item);
  if (listKind === 'pending') {
    return isBrPendingCode(raw) ? raw : 'PA';
  }
  if (raw === 'I' || raw === BR_STATUS.INVALID) {
    return 'I';
  }
  return 'R';
}

function buildAdminNameChangeDisplayItem(item, brItems, listKind) {
  const source = findNameChangeSourceBr(item, brItems);
  const isPending = listKind === 'pending';
  const statusCode = resolveNameChangeDisplayStatus(item, listKind);
  const sourceBrId = item.sourceBusinessRegistrationId
    ?? item.SourceBusinessRegistrationId
    ?? item.businessRegistrationId
    ?? item.BusinessRegistrationId
    ?? source?.businessRegistrationId
    ?? source?.BusinessRegistrationId
    ?? null;
  return toAdminDisplayItem({
    ...(source || {}),
    ...item,
    statusCode,
    requestKind: 'NAME_CHANGE',
    isNameChangePending: isPending,
    isNameChangeRequestCard: true,
    sourceBusinessRegistrationId: sourceBrId,
    displayNameEn: item.displayNameEn,
    displayNameZh: item.displayNameZh,
    mediaFileName: source?.mediaFileName || item.mediaFileName,
    ...(statusCode === 'R'
      ? { rejectReason: item.rejectReason }
      : {}),
  }, listKind);
}

/**
 * 管理端加载 BR 的唯一入口（页面勿再直接调 GetAdminPending / GetList）。
 * - GetAdminPending：待审 / 已决 / 更名（主源）
 * - GetList：仅补齐 AdminPending 未覆盖的历史照（副源；失败时 toast，不整页清空）
 * 真·单源需后端把历史并入 GetAdminPending 后再去掉副源。
 */
export async function loadAdminBrDocuments(empCompanyId) {
  if (!empCompanyId) {
    return emptyAdminLoadResult();
  }

  const langId = Number(localStorage.getItem('currentLangId')) || 1252;
  const pendingRes = await BusinessRegistrationService.GetAdminPending(empCompanyId);
  if (!pendingRes?.isSuccessful) {
    return emptyAdminLoadResult({
      error: pendingRes?.msg || 'LOAD_FAILED',
    });
  }

  let listRows = [];
  let listLoadWarning = null;
  try {
    const listRes = await BusinessRegistrationService.GetList({ empCompanyId, langId });
    if (!listRes?.isSuccessful) {
      listLoadWarning = 'LIST_LOAD_FAILED';
    } else {
      const listData = listRes?.data?.items
        || listRes?.data?.Items
        || (Array.isArray(listRes?.data) ? listRes.data : null)
        || [];
      listRows = Array.isArray(listData) ? listData : [];
    }
  } catch (e) {
    listLoadWarning = 'LIST_LOAD_FAILED';
  }

  const rows = Array.isArray(pendingRes?.data)
    ? pendingRes.data
    : (Array.isArray(pendingRes?.data?.items) ? pendingRes.data.items : []);

  const brFromAdmin = rows
    .filter((row) => {
      const kind = String(row.requestKind || row.RequestKind || '').toUpperCase();
      return kind !== 'NAME_CHANGE';
    })
    .map((row) => normalizeAdminItem(row, 'processed', 'BR_DOCUMENT'))
    .filter((item) => item.approvalId);

  const nameChanges = rows
    .filter((row) => {
      const kind = String(row.requestKind || row.RequestKind || '').toUpperCase();
      return kind === 'NAME_CHANGE';
    })
    .map((row) => normalizeAdminItem(row, 'processed', 'NAME_CHANGE'))
    .filter((item) => item.approvalId);

  const adminBrIds = new Set(
    brFromAdmin.map((x) => Number(x.approvalId)).filter((n) => Number.isFinite(n)),
  );
  const brFromList = listRows
    .map((row) => normalizeAdminItem(row, 'processed', 'BR_DOCUMENT'))
    .filter((item) => {
      const id = Number(item.approvalId);
      if (!Number.isFinite(id) || id <= 0 || adminBrIds.has(id)) {
        return false;
      }
      const code = getItemStatusCode(item);
      return isBrApprovedCode(item.statusCode)
        || code === BR_STATUS.REJECTED
        || code === BR_STATUS.INVALID
        || isBrPendingCode(item.statusCode);
    });

  const brItems = composeBrListWithNameChanges(
    [...brFromAdmin, ...brFromList],
    nameChanges,
  );

  const documentList = brItems.filter(
    (item) => !item.isNameChangeRequestCard && item.requestKind !== 'NAME_CHANGE',
  );
  const nameChangeSourceList = getApprovedBrForNameChangeOptions(documentList);

  const pendingNameChangeItems = nameChanges
    .filter((item) => isBrPendingCode(item.statusCode))
    .map((item) => buildAdminNameChangeDisplayItem(item, brItems, 'pending'));

  const rejectedNameChangeItems = nameChanges
    .filter((item) => getItemStatusCode(item) === BR_STATUS.REJECTED)
    .map((item) => buildAdminNameChangeDisplayItem(item, brItems, 'processed'));

  const invalidatedNameChangeItems = nameChanges
    .filter((item) => getItemStatusCode(item) === BR_STATUS.INVALID)
    .map((item) => buildAdminNameChangeDisplayItem(item, brItems, 'processed'));

  const nameChangeRequestItems = [
    ...pendingNameChangeItems,
    ...rejectedNameChangeItems,
    ...invalidatedNameChangeItems,
  ];
  const nameChangeRequestApprovalIds = new Set(
    nameChangeRequestItems.map((row) => String(row.approvalId)).filter(Boolean),
  );
  const shouldHideDuplicateRequestRow = (item) => {
    const approvalId = String(item.approvalId ?? '');
    return nameChangeRequestApprovalIds.has(approvalId);
  };

  const pendingItems = sortBrListItemsByLastUpdated([
    ...brItems
      .filter((item) => isBrPendingCode(item.statusCode))
      .map((item) => toAdminDisplayItem(item, 'pending'))
      .filter((item) => !shouldHideDuplicateRequestRow(item)),
    ...pendingNameChangeItems,
  ]);

  const processedItems = sortBrListItemsByLastUpdated([
    ...brItems
      .filter((item) => {
        const code = getItemStatusCode(item);
        return code === BR_STATUS.APPROVED
          || code === BR_STATUS.REJECTED
          || code === BR_STATUS.INVALID;
      })
      .map((item) => toAdminDisplayItem(item, 'processed'))
      .filter((item) => !shouldHideDuplicateRequestRow(item)),
    ...rejectedNameChangeItems,
    ...invalidatedNameChangeItems,
  ]);

  const withCurrent = [...rows]
    .map((row) => ({
      currentNameEn: row.currentNameEn || row.CurrentNameEn || '',
      currentNameZh: row.currentNameZh || row.CurrentNameZh || '',
    }))
    .find((x) => x.currentNameEn);

  return {
    pendingItems,
    processedItems,
    documentList,
    nameChangeSourceList,
    currentNameEn: withCurrent?.currentNameEn || '',
    currentNameZh: withCurrent?.currentNameZh || '',
    error: null,
    listLoadWarning,
  };
}
